import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListTodo, Eye } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import { DataTable, useDataTableState } from '@/components/admin/DataTable';
import type { ColumnDef, PaginationData } from '@/components/admin/DataTable';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface AgentTask {
  id: string;
  task_type: string;
  status: string;
  trigger_type: string | null;
  model_used: string | null;
  tool_call_count: number;
  created_at: string;
  completed_at: string | null;
}

interface TasksResponse {
  tasks: AgentTask[];
  counts?: Record<string, number>;
  pagination: PaginationData;
}

const STATUS_OPTIONS = ['', 'pending', 'planning', 'executing', 'waiting', 'completed', 'failed', 'escalated'];
const TYPE_OPTIONS = [
  '',
  'order_fulfillment',
  'catalog_sync',
  'pricing_review',
  'chat_response',
  'product_listing',
  'catalog_curation',
  'recovery',
  'scheduled_check',
  'return_coordination',
  'supplier_discovery',
  'negotiation',
];

const statusBadge: Record<string, { variant: 'green' | 'red' | 'blue' | 'yellow' | 'gray'; label: string }> = {
  pending: { variant: 'yellow', label: 'Pending' },
  planning: { variant: 'blue', label: 'Planning' },
  executing: { variant: 'blue', label: 'Executing' },
  waiting: { variant: 'yellow', label: 'Waiting' },
  completed: { variant: 'green', label: 'Completed' },
  failed: { variant: 'red', label: 'Failed' },
  escalated: { variant: 'red', label: 'Escalated' },
};

export default function AgentTasks() {
  const navigate = useNavigate();
  const addToast = useUIStore((s) => s.addToast);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';
  const typeFilter = searchParams.get('task_type') || '';
  const setStatusFilter = (v: string) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set('status', v);
    else next.delete('status');
    setSearchParams(next, { replace: true });
  };
  const setTypeFilter = (v: string) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set('task_type', v);
    else next.delete('task_type');
    setSearchParams(next, { replace: true });
  };
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1, limit: 25, total: 0, total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  const { state, updateState } = useDataTableState({
    storageKey: 'admin-agent-tasks',
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultLimit: 25,
    columns: [],
    filterKeys: [],
  });

  const fetchTasks = async (silent = false) => {
    try {
      if (!silent) { setIsLoading(true); setError(null); }
      const params = new URLSearchParams();
      params.set('page', state.page.toString());
      params.set('limit', state.limit.toString());
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('task_type', typeFilter);
      const res = await api.get<TasksResponse>(`/admin/agent/tasks?${params.toString()}`);
      setTasks(res.tasks);
      setPagination(res.pagination);
      if (res.counts) setStatusCounts(res.counts);
    } catch (err) {
      if (!silent) {
        const msg = err instanceof Error ? err.message : 'Failed to load tasks';
        setError(msg);
        addToast(msg, 'error');
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [state.page, state.limit, statusFilter, typeFilter]);

  useEffect(() => {
    const interval = setInterval(() => fetchTasks(true), 30_000);
    return () => clearInterval(interval);
  }, [state.page, state.limit, statusFilter, typeFilter]);

  const columns: ColumnDef<AgentTask>[] = [
    {
      id: 'task_type',
      header: 'Type',
      cell: ({ row }) => (
        <span className="text-sm font-mono text-gray-800">{row.task_type}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const b = statusBadge[row.status] ?? { variant: 'gray' as const, label: row.status };
        return <Badge variant={b.variant}>{b.label}</Badge>;
      },
    },
    {
      id: 'trigger_type',
      header: 'Trigger',
      cell: ({ row }) => (
        <span className="text-sm text-gray-500">{row.trigger_type ?? '—'}</span>
      ),
    },
    {
      id: 'model_used',
      header: 'Model Used',
      cell: ({ row }) => (
        <span className="text-xs font-mono text-gray-500">{row.model_used ?? '—'}</span>
      ),
    },
    {
      id: 'tool_call_count',
      header: 'Tool Calls',
      align: 'right',
      cell: ({ row }) => (
        <span className="text-sm text-gray-700">{row.tool_call_count}</span>
      ),
    },
    {
      id: 'created_at',
      header: 'Created',
      sortable: true,
      cell: ({ row }) => (
        <span className="text-sm text-gray-500">
          {new Date(row.created_at).toLocaleString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/agent/tasks/${row.id}`);
          }}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          aria-label={`View task ${row.id}`}
        >
          <Eye className="w-4 h-4" />
          View
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Agent Tasks</h1>
        <p className="text-gray-600 mt-1">Browse all tasks executed by the AI Dropshipping Agent.</p>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {STATUS_OPTIONS.map((s) => {
                const label = s === '' ? 'All statuses' : s.charAt(0).toUpperCase() + s.slice(1);
                const n = s === '' ? undefined : statusCounts[s];
                return (
                  <option key={s} value={s}>
                    {n != null ? `${label} (${n})` : label}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Task Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t === '' ? 'All types' : t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <DataTable
        columns={columns}
        data={tasks}
        pagination={pagination}
        isLoading={isLoading}
        error={error}
        state={state}
        onStateChange={updateState}
        rowKey="id"
        searchPlaceholder="Search tasks..."
        emptyState={
          <div className="p-12 text-center">
            <ListTodo className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-600">Tasks are created when the agent processes orders or runs scheduled jobs.</p>
          </div>
        }
      />
    </div>
  );
}
